<template>
  <!-- Calendario de la cita de firma del encargo online (ref: https://trello.com/c/Bnl5pAM2). Mismo calendario propio que el
       contacto guiado (ref: https://trello.com/c/mm9XRZhS), pero solo con los días y horas que permite la notaría. -->
  <div ref="raiz" class="ccita">
    <v-menu v-model="abierto" :close-on-content-click="false" location="bottom start" min-width="0">
      <template #activator="{ props: activador }">
        <div class="ccita__campo" v-bind="activador">
          <input :id="id" :value="textoValor" type="text" readonly class="ccita__input" autocomplete="off"
            :placeholder="reglas.modo === 'DIA' ? 'dd/mm/aaaa' : 'dd/mm/aaaa hh:mm'" :aria-label="etiqueta"
            @keydown.enter.prevent="abierto = true" @keydown.space.prevent="abierto = true" />
          <span class="ccita__icono" aria-hidden="true"><v-icon size="20">mdi-calendar</v-icon></span>
        </div>
      </template>
      <v-locale-provider :locale="localeVuetify" fallback-locale="en" :messages="mensajesVuetify">
        <div class="ccita__panel" :style="{ '--ccita-acento': color }">
          <v-date-picker :model-value="fechaDeIso(modelValue?.fecha)" :color="color" hide-header show-adjacent-months
            :min="reglas.desde" :max="reglas.hasta" :allowed-dates="permitido" @update:model-value="elegirFecha" />
          <div v-if="reglas.modo === 'DIA_HORA' && modelValue?.fecha" class="ccita__horas">
            <p class="ccita__horas-titulo">{{ t('Hora') }}</p>
            <div class="ccita__horas-lista">
              <button v-for="h in reglas.horas" :key="h" type="button" class="ccita__chip" :class="{ activo: modelValue?.hora === h }"
                @click="elegirHora(h)">{{ h }}</button>
            </div>
          </div>
        </div>
      </v-locale-provider>
    </v-menu>
    <!-- Día y hora: si cierra el calendario sin elegir la hora, se le avisa aquí mismo y puede volver a abrirlo -->
    <button v-if="faltaHora" type="button" class="ccita__falta" @click="abierto = true">
      <v-icon size="16">mdi-clock-alert-outline</v-icon>{{ t('Elija también la hora de la cita.') }}
    </button>
    <button v-if="modelValue?.fecha" type="button" class="ccita__quitar" @click="emit('update:modelValue', null)">
      <v-icon size="16">mdi-close</v-icon>{{ t('Quitar la cita') }}
    </button>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { t } from '../../composables/solicitudTextos'
import { useIdiomaVuetify } from '../../composables/vuetifyIdioma'
import type { CitaPropuesta, ReglasCitaTramite } from '../../types/solicitudWeb'

const props = withDefaults(defineProps<{
  modelValue: CitaPropuesta | null | undefined
  reglas: ReglasCitaTramite
  id?: string
  etiqueta?: string
  /** Variable CSS del contenedor con el color de acento (el menú se abre fuera de él). */
  variableAcento?: string
}>(), { id: 'cita-firma', etiqueta: '', variableAcento: '--sw-acento' })
const emit = defineEmits<{ (e: 'update:modelValue', v: CitaPropuesta | null): void }>()

const { locale: localeVuetify, messages: mensajesVuetify } = useIdiomaVuetify()
const abierto = ref(false)
const raiz = ref<HTMLElement | null>(null)
const color = ref('#1f3a5f')
watch(abierto, (v) => {
  if (!v || !raiz.value) return
  const c = getComputedStyle(raiz.value).getPropertyValue(props.variableAcento).trim()
  if (c) color.value = c
})

const isoLocal = (f: Date) => `${f.getFullYear()}-${String(f.getMonth() + 1).padStart(2, '0')}-${String(f.getDate()).padStart(2, '0')}`
function fechaDeIso(valor?: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(valor || '')
  return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : null
}

/** Días de la semana de la notaría (1 = lunes … 7 = domingo) y sin los días cerrados; el rango lo ponen min y max. */
function permitido(valor: unknown): boolean {
  const f = valor instanceof Date ? valor : fechaDeIso(String(valor ?? '').slice(0, 10))
  if (!f || isNaN(f.getTime())) return false
  const iso = isoLocal(f)
  if (iso < props.reglas.desde || iso > props.reglas.hasta) return false
  const dia = f.getDay() === 0 ? 7 : f.getDay()
  return props.reglas.diasSemana.includes(dia) && !props.reglas.diasCerrados.includes(iso)
}

function elegirFecha(valor: unknown) {
  const f = Array.isArray(valor) ? valor[0] : valor
  if (!(f instanceof Date) || isNaN(f.getTime())) return
  const hora = props.reglas.modo === 'DIA_HORA' ? (props.modelValue?.hora ?? '') : ''
  emit('update:modelValue', { fecha: isoLocal(f), hora })
  // Solo el día: se cierra al elegirlo; con hora, al elegir la hora
  if (props.reglas.modo !== 'DIA_HORA' || hora) abierto.value = false
}

function elegirHora(hora: string) {
  if (!props.modelValue?.fecha) return
  emit('update:modelValue', { fecha: props.modelValue.fecha, hora })
  abierto.value = false
}

const faltaHora = computed(() => props.reglas.modo === 'DIA_HORA' && !!props.modelValue?.fecha && !props.modelValue?.hora && !abierto.value)

const textoValor = computed(() => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(props.modelValue?.fecha || '')
  if (!m) return ''
  return `${m[3]}/${m[2]}/${m[1]}` + (props.modelValue?.hora ? ' ' + props.modelValue.hora : '')
})
</script>

<style lang="scss">
.ccita {
  &__campo { position: relative; display: flex; align-items: center; max-width: 320px; }
  &__input {
    width: 100%; padding: 12px 42px 12px 14px; border: 1.5px solid #cfd6dc; border-radius: 10px; font-size: 16px;
    background: #fff; color: #1d2329; cursor: pointer; font-family: inherit;
    &:focus { border-color: var(--sw-acento, #1f3a5f); outline: none; }
  }
  &__icono { position: absolute; right: 12px; display: inline-flex; color: var(--sw-acento, #1f3a5f); pointer-events: none;
    .v-icon { color: inherit !important; } }
  &__panel { background: #fff; color: #1d2329; border: 1px solid #e3e7eb; border-radius: 14px; box-shadow: 0 16px 40px rgba(20, 30, 40, .16);
    max-width: calc(100vw - 32px); overflow: hidden;
    .v-date-picker { background: transparent; } }
  &__horas { padding: 0 16px 16px; max-width: 328px; }
  &__horas-titulo { margin: 0 0 8px; font-weight: 700; font-size: 15px; }
  &__horas-lista { display: grid; grid-template-columns: repeat(4, 1fr); gap: 6px; max-height: 164px; overflow-y: auto; }
  &__chip {
    display: inline-flex; justify-content: center; align-items: center; padding: 6px 4px; min-height: 34px; font-size: 14px;
    border: 1.5px solid #cfd6dc; border-radius: 999px; background: #fff; color: #1d2329; cursor: pointer;
    &.activo { background: var(--ccita-acento, #1f3a5f); border-color: var(--ccita-acento, #1f3a5f); color: #fff; font-weight: 600; }
  }
  &__falta {
    display: flex; align-items: center; gap: 4px; margin-top: 6px; padding: 0; border: 0; background: none;
    color: rgb(var(--v-theme-error, 176, 0, 32)); font-size: 0.88rem; font-weight: 600; cursor: pointer; text-align: start;
  }
  &__quitar {
    display: inline-flex; align-items: center; gap: 4px; margin-top: 6px; padding: 0; border: 0; background: none;
    color: var(--sw-tenue, #5f6b76); font-size: 0.88rem; cursor: pointer; text-decoration: underline;
  }
}
</style>
